// Pure helpers for the dark-mode persistence layer used by UserMenu.
//
// JSX-free so the node-pool tests can verify the localStorage round-
// trip + the html.dark class manipulation logic without a DOM.
// `applyDarkModeClass` accepts an optional documentElement override
// so tests can pass a stub.
//
// Defaults reach into globalThis (the standard cross-runtime global)
// rather than `window` / `document` directly so the file type-checks
// cleanly under both the root tsconfig (no DOM lib — code path runs
// in the migration / worker pools) and the web tsconfig (full DOM).

const DARK_MODE_LS_KEY = "stewardshipos.darkMode";

interface ClassListLike {
  add: (cls: string) => void;
  remove: (cls: string) => void;
}
interface DocumentLike {
  classList: ClassListLike;
}

interface StorageLike {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
}

interface BrowserGlobals {
  localStorage?: StorageLike;
  document?: { documentElement?: DocumentLike };
}

function browserGlobals(): BrowserGlobals {
  return globalThis as unknown as BrowserGlobals;
}

function defaultStorage(): StorageLike | null {
  try {
    return browserGlobals().localStorage ?? null;
  } catch {
    return null;
  }
}

function defaultDocumentElement(): DocumentLike | null {
  return browserGlobals().document?.documentElement ?? null;
}

// Read the effective initial dark-mode preference. Dark ONLY on an
// explicit stored opt-in ("1"); anything else — "0", absent, garbage,
// unreadable storage — is light.
//
// DECISION (Grant, 2026-07-23): we deliberately do NOT fall back to the
// OS prefers-color-scheme. The earlier system-preference fallback
// (audit item 6) auto-served the dark theme to first-time users on
// dark-mode Macs (Jake's washed-out first run, while dashboards still
// carried hardcoded light-theme hex). Dark mode is opt-in via the
// avatar-menu toggle. Keep this logic in sync with the pre-hydration
// script in index.html, which applies the same rule before React mounts
// to avoid a flash.
export function readInitialDarkMode(storage: StorageLike | null = defaultStorage()): boolean {
  let stored: string | null = null;
  if (storage) {
    try {
      stored = storage.getItem(DARK_MODE_LS_KEY);
    } catch {
      stored = null;
    }
  }
  return stored === "1";
}

export function writeDarkModePreference(
  isDark: boolean,
  storage: StorageLike | null = defaultStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(DARK_MODE_LS_KEY, isDark ? "1" : "0");
  } catch {
    // Quota / private mode — silently no-op.
  }
}

// Toggle the `dark` class on the supplied documentElement (or the
// real one when not provided). The dark token block in styles.css is
// scoped under html.dark, so this class IS the theme switch.
export function applyDarkModeClass(
  isDark: boolean,
  doc: DocumentLike | null = defaultDocumentElement(),
): void {
  if (!doc) return;
  if (isDark) {
    doc.classList.add("dark");
  } else {
    doc.classList.remove("dark");
  }
}

export const DARK_MODE_STORAGE_KEY = DARK_MODE_LS_KEY;

// ── Who is offered PM Guidelines and the KlinkLoans Fund ──────────────────
//
// Both rows lived in the avatar menu until the 2026-10-15 navigation
// overhaul moved them to Company tools → Mortgage (sidebar-data.ts), which
// still calls this to decide who sees them.
//
// It matches the server's gate — mortgage company scope, or one of the two
// global roles, which `editable_company_slugs: "all"` already represents on
// the session. It is NOT the control: POST/GET/PATCH on /api/pm-guidelines
// each run the same check server-side, so typing the URL gets the same
// refusal. Hiding the link only spares the other fifteen people a menu item
// that would refuse them.
export function mayReachPmGuidelines(
  editableCompanySlugs: string[] | "all" | undefined | null,
): boolean {
  if (editableCompanySlugs === "all") return true;
  if (!Array.isArray(editableCompanySlugs)) return false;
  return editableCompanySlugs.includes("mortgage");
}
